// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the record of a briefing (Situation room, slice CR3,
 * docs/AYNI_XR_CONTROL_ROOM_PLAN.md): what was shown, in order, on which
 * frame of which stream and how old that data was, and what was decided.
 * The record is a `briefing` Khipu:
 *
 * - its **path** replays the briefing as it was given: each step shown, with
 *   the date it showed (not "the latest", which will have moved on), and
 *   the decision as a caption on the step where it was taken;
 * - its **log** keeps the events with their time from the start; sealed
 *   with `khipuIntegrity.sealKhipu`, each event chains the one before, so a
 *   changed or removed event shows;
 * - **names** only when the briefer chooses "with my name" (owner rule,
 *   5 October 2026: nothing personal unless switched on); then their
 *   display name is on every event, as in a research record.
 *
 * Pure: the caller says what each globe showed.
 */

import type { Khipu, KhipuDatasetRef, KhipuEvent, KhipuStep, KhipuText } from './khipu'
import { KHIPU_SCHEMA_VERSION } from './khipu'
import type { FreshnessLevel } from './streamFreshness'
import { DECISION_PROMPT } from './situationTemplates'

/** One globe as a step left it. */
export interface BriefingGlobeShown {
  datasetId: string
  title: string
  /** The frame on screen, epoch ms; null for a dataset without dates. */
  frameMs: number | null
  freshness: FreshnessLevel | null
}

export interface BriefingStepShown {
  stepId: string
  title: string
  /** Ms from the start. */
  t: number
  globes: BriefingGlobeShown[]
}

export interface BriefingDecision {
  stepId: string
  text: string
  t: number
}

/** The most a decision may say. */
export const DECISION_MAX_CHARS = 2000

const iso = (ms: number): string => new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z')

export interface BriefingRecorder {
  /** A step went up (again: a later showing is recorded too). */
  stepShown(stepId: string, title: string, globes: BriefingGlobeShown[]): void
  /** The decision taken on a step; a later one on the same step replaces it. Empty text removes it. */
  decide(stepId: string, text: string): void
  shown(): readonly BriefingStepShown[]
  decisions(): readonly BriefingDecision[]
  startedAt(): number
  build(opts: { id: string; now: number; attributed: { name: string; enabledAt: number } | null; lang: string }): Khipu
}

export function createBriefingRecorder(template: Khipu, clock: () => number = Date.now): BriefingRecorder {
  const start = clock()
  const shownSteps: BriefingStepShown[] = []
  const decided = new Map<string, BriefingDecision>()
  const events: Array<{ t: number; type: string; step?: string; data: Record<string, unknown> }> = [{ t: 0, type: 'briefing.start', data: { from: template.id } }]
  const since = (): number => Math.max(0, Math.round(clock() - start))

  return {
    stepShown(stepId, title, globes) {
      const t = since()
      shownSteps.push({ stepId, title, t, globes: globes.map((g) => ({ ...g })) })
      events.push({
        t,
        type: 'step.show',
        step: stepId,
        data: { globes: globes.map((g) => ({ dataset: g.datasetId, title: g.title, frame: g.frameMs === null ? null : iso(g.frameMs), freshness: g.freshness })) },
      })
    },
    decide(stepId, raw) {
      const text = raw.replace(/\r\n?/g, '\n').trim().slice(0, DECISION_MAX_CHARS)
      const t = since()
      if (!text) {
        if (decided.delete(stepId)) events.push({ t, type: 'decision.clear', step: stepId, data: {} })
        return
      }
      if (decided.get(stepId)?.text === text) return
      decided.set(stepId, { stepId, text, t })
      events.push({ t, type: 'decision', step: stepId, data: { text } })
    },
    shown: () => shownSteps,
    decisions: () => [...decided.values()].sort((a, b) => a.t - b.t),
    startedAt: () => start,
    build({ id, now, attributed, lang }) {
      const who = attributed?.name.trim().slice(0, 80) || null
      // Datasets: the template's, then any other shown, by their id in this app.
      const datasets: KhipuDatasetRef[] = template.datasets.map((d) => ({ ...d }))
      const keyOfId = new Map<string, string>()
      for (const d of datasets) if (typeof d.dsaId === 'string') keyOfId.set(`R2_DASH_${d.dsaId}`, d.key)
      for (const s of shownSteps) for (const g of s.globes) {
        if (keyOfId.has(g.datasetId)) continue
        const key = `app${keyOfId.size + 1}`
        keyOfId.set(g.datasetId, key)
        datasets.push({ key, ref: `app:${g.datasetId}`, appId: g.datasetId, title: { [lang]: g.title } })
      }
      // The path: each showing as it was, on the date it showed, with its decision.
      const steps: KhipuStep[] = shownSteps.map((s, i) => {
        const original = template.path.steps.find((st) => st.id === s.stepId)
        const [first, ...others] = s.globes
        const decision = decided.get(s.stepId)
        const lastShowing = !shownSteps.slice(i + 1).some((x) => x.stepId === s.stepId)
        const prompts = (original?.prompts ?? []).filter((p) => p.kind !== DECISION_PROMPT)
        if (decision && lastShowing) prompts.push({ kind: 'decision', audience: 'everyone', text: { [lang]: decision.text } as KhipuText })
        const { advance: _advance, time: _time, globes: _globes, ...rest } = original ?? { id: s.stepId, n: i + 1 }
        return {
          ...rest,
          id: `r${i + 1}`,
          n: i + 1,
          title: { [lang]: s.title },
          dataset: first ? keyOfId.get(first.datasetId) ?? null : null,
          time: first && first.frameMs !== null ? { at: iso(first.frameMs), playing: false } : null,
          ...(others.length ? { globes: others.map((g) => ({ dataset: keyOfId.get(g.datasetId) ?? null, ...(g.frameMs !== null ? { time: `=${iso(g.frameMs).slice(0, 10)}` } : {}) })) } : {}),
          prompts,
        }
      })
      const log: KhipuEvent[] = [...events, { t: Math.max(0, Math.round(now - start)), type: 'briefing.end', data: {} }]
        .map((e) => ({ ...e, actor: 'presenter', ...(who ? { who } : {}) }))
      const day = iso(start).slice(0, 16).replace('T', ' ')
      const baseTitle = Object.fromEntries(Object.entries(template.title).map(([l, v]) => [l, `${v} · ${day} UTC`]))
      return {
        format: 'ayni-khipu',
        schemaVersion: KHIPU_SCHEMA_VERSION,
        id,
        kind: 'briefing',
        status: 'published',
        title: baseTitle,
        languages: template.languages ?? [lang],
        created: iso(start),
        modified: iso(now),
        derivedFrom: [{ id: template.id, relation: 'recorded-from', title: template.title }],
        privacy: who
          ? {
              mode: 'attributed',
              captured: { names: true, chatText: false, voice: false, camera: false, perPersonActions: false, perPersonAnswers: false },
              enabledAt: iso(attributed!.enabledAt),
              lawfulBasis: 'consent',
              noticeShown: true,
            }
          : { mode: 'anonymous', captured: { names: false, chatText: false, voice: false, camera: false, perPersonActions: false, perPersonAnswers: false } },
        datasets,
        path: { steps: steps.length ? steps : [{ id: 'r1', n: 1, dataset: null }] },
        log: { startedAt: iso(start), endedAt: iso(now), events: log },
      }
    },
  }
}
